import { z } from "zod";
import { optionalEnum, optionalUuid } from "./_helpers/optional";

export const AccountSchema = z.object({
  id: z.string().uuid().describe("ID"),
  name: z.string().describe("Name"),
  slug: z.string().describe("Slug"),
  billing_contact_id: z
    .string()
    .uuid()
    .nullable()
    .optional()
    .describe("Billing Contact"),
  plan_type: z
    .enum(["starter", "professional", "enterprise"])
    .describe("Plan Type"),
  plan_started_at: z.string().nullable().describe("Plan Started At"),
  plan_expires_at: z.string().nullable().describe("Plan Expires At"),
  status: z
    .enum(["active", "suspended", "cancelled", "trial"])
    .describe("Status"),
  settings: z.record(z.unknown()).describe("Settings"),
  logo_url: z.string().nullable().describe("Logo URL"),
  primary_color: z.string().nullable().describe("Primary Color"),
  secondary_color: z.string().nullable().describe("Secondary Color"),
  is_demo: z.boolean().describe("Is Demo"),
  demo_created_by: z.string().uuid().nullable().describe("Demo Created By"),
  demo_loaded_at: z.string().datetime().nullable().describe("Demo Loaded At"),
  created_at: z.string().describe("Created At"),
  updated_at: z.string().describe("Updated At"),
});
export type Account = z.infer<typeof AccountSchema>;

// Account enriched with aggregate counts from core.accounts_with_counts.
export const AccountWithCountsSchema = AccountSchema.extend({
  business_count: z.number().int().nonnegative().describe("Business Count"),
  user_count: z.number().int().nonnegative().describe("User Count"),
});
export type AccountWithCounts = z.infer<typeof AccountWithCountsSchema>;

// Payload for super-admin account creation (POST /api/v1/core/accounts).
export const CreateAccountBodySchema = z.object({
  name: z.string().min(1).describe("Account Name"),
  slug: z
    .string()
    .min(1)
    .regex(/^[a-z0-9-]+$/)
    .optional()
    .describe("Slug"),
  admin_email: z.string().email().describe("Admin Email"),
  admin_first_name: z.string().optional().describe("Admin First Name"),
  admin_last_name: z.string().optional().describe("Admin Last Name"),
  // ADV-1396: required on the HTTP payload so the invited admin's JWT always
  // gets a business_id claim. The createAccount service and demo path keep
  // this optional — enforcement is at the HTTP schema + UI layer only.
  // The name must contain at least one alphanumeric so slugifyName() can
  // derive a non-empty slug; otherwise createBusiness returns an empty-slug
  // error, createAccount logs it but still returns 201, and the admin lands
  // with no business_id claim again — the exact redirect loop this ticket fixes.
  first_business_name: z
    .string()
    .min(1)
    .regex(/[a-z0-9]/i, "Must contain at least one letter or number")
    .describe("First Business Name"),
  first_business_slug: z
    .string()
    .regex(/^[a-z0-9-]+$/)
    .optional()
    .describe("First Business Slug"),
  first_business_vertical: z
    .string()
    .optional()
    .describe("First Business Vertical"),
  // ADV-387: force-link path. When set, skip Supabase Auth invite for the
  // admin and insert the account_users row with this auth.users id. Super
  // admin resolves the id via GET /core/admin/users/lookup-by-email first.
  force_link_existing_auth_user_id: optionalUuid().describe(
    "Link Existing Auth User",
  ),
});
export type CreateAccountBody = z.infer<typeof CreateAccountBodySchema>;

export const DemoAccountRequestSchema = z.object({
  // ADR-0044: optionalEnum coerces "" → undefined so the downstream
  // `.default(...)` still applies; null passes through and is normalized to the
  // default by the service's `?? "small"` / `?? "fast"` coalesce.
  size: optionalEnum(["small", "medium", "large"] as const)
    .default("small")
    .describe("Size"),
  mode: optionalEnum(["fast", "full"] as const)
    .default("fast")
    .describe("Mode"),
});
export type DemoAccountRequest = z.infer<typeof DemoAccountRequestSchema>;

export const DemoAccountResponseSchema = z.object({
  account_id: z.string().uuid().describe("Account ID"),
  batch_id: z.string().uuid().nullable().describe("Batch ID"),
  status: z.literal("provisioning").describe("Status"),
  existing: z.boolean().describe("Existing"),
});
export type DemoAccountResponse = z.infer<typeof DemoAccountResponseSchema>;

export const BusinessSchema = z.object({
  id: z.string().uuid().describe("ID"),
  account_id: z.string().uuid().describe("Account"),
  name: z.string().describe("Name"),
  slug: z.string().describe("Slug"),
  vertical: z.string().nullable().describe("Vertical"),
  description: z.string().nullable().describe("Description"),
  contact_id: z.string().uuid().nullable().optional().describe("Contact"),
  website: z.string().nullable().describe("Website"),
  logo_url: z.string().nullable().describe("Logo URL"),
  icon_url: z.string().nullable().describe("Icon URL"),
  primary_color: z.string().nullable().describe("Primary Color"),
  status: z.enum(["active", "inactive", "setup"]).describe("Status"),
  settings: z.record(z.unknown()).describe("Settings"),
  created_at: z.string().describe("Created At"),
  updated_at: z.string().describe("Updated At"),
});
export type Business = z.infer<typeof BusinessSchema>;

// Payload for owner-level business creation (POST /api/v1/core/businesses).
export const CreateBusinessBodySchema = z.object({
  name: z.string().min(1).max(255).describe("Business Name"),
  vertical: z.string().max(100).optional().describe("Vertical"),
  description: z.string().max(1000).optional().describe("Description"),
  website: z.string().optional().describe("Website"),
  industry_pack: z
    .object({
      slug: z.string().describe("Pack Slug"),
      item_selection: z
        .object({
          field_keys: z.array(z.string()).optional().describe("Field Keys"),
          apply_commission: z.boolean().optional().describe("Apply Commission"),
        })
        .optional()
        .describe("Item Selection"),
    })
    .optional()
    .describe("Industry Pack"),
});
export type CreateBusinessBody = z.infer<typeof CreateBusinessBodySchema>;

export const CreateBusinessResponseSchema = z.object({
  business: BusinessSchema,
  pack_result: z
    .object({
      fields_created: z.number().int().describe("Fields Created"),
      commission_plan_created: z.boolean().describe("Commission Plan Created"),
    })
    .optional()
    .describe("Pack Result"),
});
export type CreateBusinessResponse = z.infer<
  typeof CreateBusinessResponseSchema
>;

export const UpdateBusinessSchema = z.object({
  name: z.string().min(1).optional().describe("Name"),
  website: z.string().nullable().optional().describe("Website"),
  description: z.string().nullable().optional().describe("Description"),
  vertical: z.string().nullable().optional().describe("Vertical"),
  logo_url: z.string().nullable().optional().describe("Logo URL"),
  icon_url: z.string().nullable().optional().describe("Icon URL"),
  primary_color: z.string().nullable().optional().describe("Primary Color"),
  status: optionalEnum(["active", "inactive"] as const).describe("Status"),
  settings: z.record(z.unknown()).optional().describe("Settings"),
});
export type UpdateBusinessInput = z.infer<typeof UpdateBusinessSchema>;

export const AccountUserSchema = z.object({
  id: z.string().uuid().describe("ID"),
  account_id: z.string().uuid().describe("Account"),
  contact_id: z.string().uuid().nullable().optional().describe("Contact"),
  email: z.string().email().describe("Email"),
  first_name: z.string().nullable().describe("First Name"),
  last_name: z.string().nullable().describe("Last Name"),
  phone: z.string().nullable().describe("Phone"),
  role: z.enum(["owner", "admin", "billing", "viewer"]).describe("Role"),
  all_businesses_access: z.boolean().describe("All Businesses Access"),
  auth_user_id: z.string().nullable().describe("Auth User"),
  status: z.enum(["active", "inactive", "invited"]).describe("Status"),
  invited_at: z.string().nullable().describe("Invited At"),
  accepted_at: z.string().nullable().describe("Accepted At"),
  created_at: z.string().describe("Created At"),
  updated_at: z.string().describe("Updated At"),
});
export type AccountUser = z.infer<typeof AccountUserSchema>;

export const AccountUserBusinessSchema = z.object({
  id: z.string().uuid().describe("ID"),
  account_user_id: z.string().uuid().describe("Account User"),
  business_id: z.string().uuid().describe("Business"),
  role: z
    .enum(["admin", "supervisor", "member", "worker", "viewer"])
    .nullable()
    .describe("Role"),
  created_at: z.string().describe("Created At"),
});
export type AccountUserBusiness = z.infer<typeof AccountUserBusinessSchema>;

// ─── Import Settings ─────────────────────────────────────────────────────────

export const ImportSettingsSchema = z.object({
  auto_approve_imports: z.boolean().describe("Auto-Approve Imports"),
  auto_approve_dedup: z.boolean().describe("Auto-Approve Dedup"),
  auto_approve_address_corrections: z
    .boolean()
    .describe("Auto-Approve Address Corrections"),
  notification_channel: z
    .enum(["email", "in_app", "both"])
    .describe("Notification Channel"),
  notification_frequency: z
    .enum(["batched", "daily_digest"])
    .describe("Notification Frequency"),
  notification_batch_window_minutes: z
    .number()
    .int()
    .min(1)
    .describe("Notification Batch Window Minutes"),
  notification_emails: z
    .array(z.string().email())
    .default([])
    .describe("Notification Emails"),
  review_timeout_days: z.number().int().min(1).describe("Review Timeout Days"),
});
export type ImportSettings = z.infer<typeof ImportSettingsSchema>;

export const IMPORT_SETTINGS_DEFAULTS: ImportSettings = {
  auto_approve_imports: false,
  auto_approve_dedup: false,
  auto_approve_address_corrections: true,
  notification_channel: "email",
  notification_frequency: "batched",
  notification_batch_window_minutes: 15,
  notification_emails: [],
  review_timeout_days: 7,
};

export const ImportSettingsPatchSchema = ImportSettingsSchema.partial();
export type ImportSettingsPatch = z.infer<typeof ImportSettingsPatchSchema>;

// ADV-1135 — Per-business media bandwidth settings (soft-throttle cap).

/** Default daily byte cap per business (10 GiB). */
export const DEFAULT_DAILY_BYTES_PER_BUSINESS = 10 * 1024 * 1024 * 1024;

export const MediaSettingsSchema = z.object({
  daily_bytes_cap: z.number().int().nonnegative().describe("Daily Bytes Cap"),
});
export type MediaSettings = z.infer<typeof MediaSettingsSchema>;

export const MEDIA_SETTINGS_DEFAULTS: MediaSettings = {
  daily_bytes_cap: DEFAULT_DAILY_BYTES_PER_BUSINESS,
};

/** Patch shape accepts `null` to revert a field to the default. */
export const MediaSettingsPatchSchema = z.object({
  daily_bytes_cap: z
    .number()
    .int()
    .nonnegative()
    .nullable()
    .optional()
    .describe("Daily Bytes Cap"),
});
export type MediaSettingsPatch = z.infer<typeof MediaSettingsPatchSchema>;

export const BusinessSettingsSchema = z.object({
  import_settings: ImportSettingsSchema.optional().describe("Import Settings"),
  media_settings: MediaSettingsSchema.optional().describe("Media Settings"),
});
export type BusinessSettings = z.infer<typeof BusinessSettingsSchema>;

export const UpdateBusinessSettingsSchema = z.object({
  import_settings:
    ImportSettingsPatchSchema.optional().describe("Import Settings"),
  media_settings:
    MediaSettingsPatchSchema.optional().describe("Media Settings"),
});
export type UpdateBusinessSettingsInput = z.infer<
  typeof UpdateBusinessSettingsSchema
>;
